/**
 * useQuotesData Hook
 * ==================
 * Fetches completed RFQs/Quotes with linked invoice information.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';

export interface Quote {
    id: string;
    title: string;
    completed_at: string;
    status: string;
    company_id: string;

    // Supplier info
    selected_supplier?: {
        supplier_id?: string;
        name?: string;
        region?: string;
    };

    // Offer summary
    offer_count?: number;
    final_amount?: number;
    currency?: string;

    // Invoice linkage
    has_invoice?: boolean;
    linked_invoice_count?: number;
}

interface UseQuotesDataReturn {
    data: Quote[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
        has_next: boolean;
        has_prev: boolean;
    };
    fetchData: (params: any) => void;
}

export const useQuotesData = (): UseQuotesDataReturn => {
    const { token } = useAuth();
    const [data, setData] = useState<Quote[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
        has_next: false,
        has_prev: false
    });

    const fetchData = useCallback(async (params: any = {}) => {
        if (!token) return;

        setIsLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();
            queryParams.append('page', (params.page || 1).toString());
            queryParams.append('page_size', (params.limit || 20).toString());

            const response = await fetch(`/api/invoices/completed-rfqs?${queryParams.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch quotes');
            }

            const result = await response.json();

            if (result.success && result.data) {
                const { rfqs, total, page, page_size, has_next, has_prev } = result.data;

                // Transform RFQ data to Quote format
                const quotes: Quote[] = rfqs.map((rfq: any) => ({
                    id: rfq.id || rfq._id,
                    title: rfq.title || 'Untitled RFQ',
                    completed_at: rfq.completed_at,
                    status: rfq.status || 'completed',
                    company_id: rfq.company_id,
                    selected_supplier: rfq.selected_supplier,
                    offer_count: rfq.offer_count || 0,
                    final_amount: rfq.final_amount || rfq.total_amount,
                    currency: rfq.currency || 'USD',
                    has_invoice: rfq.has_invoice || false,
                    linked_invoice_count: rfq.linked_invoice_count || 0
                }));

                setData(quotes);
                setPagination({
                    page,
                    limit: page_size,
                    total,
                    pages: Math.ceil(total / page_size),
                    has_next,
                    has_prev
                });
            } else {
                setError(result.message || 'Error fetching data');
            }
        } catch (err: any) {
            setError(err.message || 'Network error');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    // Initial fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, isLoading, error, pagination, fetchData };
};
